import React, {useEffect, useState, memo} from "react";
import {
  Button,
  createStyles,
  makeStyles,
  Theme
} from "@material-ui/core";
import {
  CursorTooltip,
  DraggableDialog,
  PromptDialog
} from "../components";
import { MultiImageOptions } from "../viewer/js/controls";
import LocalScene from "../viewer/js/projections";
import { roundDigit, UnitConverter } from "../viewer/js/utilities";
import { viewerBackground } from "../viewer/viewer";
import { useTranslation } from "react-i18next";
import { useViewer } from "../hooks";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gradient: {
      backgroundImage: viewerBackground
    },
    buttonParentLeft: {
      position: "absolute",
      bottom: "0px",
    },
    buttonParentRight: {
      position: "absolute",
      bottom: "0px",
      right: "0px"
    },
    smallButton: {
      padding: theme.spacing(0.5, 1),
      margin: theme.spacing(0.75),
      fontSize: "0.6rem"
    },
    buttonRight: {
      float: "right"
    },
    multiImageHeader: {
      position: "absolute",
      display: "inline-block",
      background: "rgba(255,255,255,0.75)",
      boxShadow: "5px 5px 5px rgb(0 0 0 / 25%)",
      margin: "10px",
      padding: "10px",
      top: "0px",
      borderRadius: "3px",
      fontSize: "0.8em",
      userSelect: "none",
      fontFamily: theme.typography.fontFamily,
      "& div:not(:last-child)": {
        paddingBottom: theme.spacing(0.75)
      }
    }
  }),
);

const WindowButton = (props) => {
  const {text, hidden, disabled, onClick, color} = props;

  const classes = useStyles();

  return (
    <React.Fragment>
      {!hidden && (<Button
        className={classes.smallButton}
        variant="contained"
        disabled={disabled}
        color={color}
        onClick={onClick}
      >{text}</Button>)}
    </React.Fragment>
  );
};

const MultiImageInfo = (props) => {
  const {state} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const clampedColor = (value, minValue, midValue) => {
    if (value < minValue) {
      return "green";
    }

    if (value < midValue) {
      return "#f89406";
    }

    return "red";
  };

  const {coordinates, intersect} = state;
  const {error, distances, calculated, valid, message} = intersect;
  const units = LocalScene.dataProjectionUnits;
  const converter = new UnitConverter();

  const observationStyle1 = {color: coordinates[0] ? "green" : "red"};
  const observationStyle2 = {color: coordinates[1] ? "green" : "red"};

  const observationText1 = coordinates[0]
    ? t('measure.selected')
    : t('measure.not-selected');

  const observationText2 = coordinates[1]
    ? t('measure.selected')
    : t('measure.not-selected');

  const errorStyle = {color: clampedColor(error, 0.1, 0.25)};
  const errorValue = roundDigit(converter.convert(error, 'm', units), 4);
  const errorElement = <i style={errorStyle}>{errorValue}{units}</i>;

  const distanceStyle1 = {color: clampedColor(distances[0], 0.1, 0.25)};
  const distanceValue1 = roundDigit(converter.convert(distances[0], 'm', units), 2);
  const distanceElement1 = <i style={distanceStyle1}>{distanceValue1}{units}</i>;

  const distanceStyle2 = {color: clampedColor(distances[1], 0.1, 0.25)};
  const distanceValue2 = roundDigit(converter.convert(distances[1], 'm', units), 2);
  const distanceElement2 = <i style={distanceStyle2}>{distanceValue2}{units}</i>;

  return (
    <div className={classes.multiImageHeader}>

      {/* Calculated information */}
      {calculated && (<React.Fragment>
        <div>
          {`${t('measure.estimated-error')}: `}
          {errorElement}
        </div>

        <div>
          {`${t('measure.camera-distance-1')}: `}
          {distanceElement1}
        </div>

        <div>
          {`${t('measure.camera-distance-2')}: `}
          {distanceElement2}
        </div>
      </React.Fragment>)}

      {/* Observation status */}
      {!calculated && (<React.Fragment>

        <div>
          {`${t('measure.observation-1')}: `}
          <i style={observationStyle1}>{observationText1}</i>
        </div>

        <div>
          {`${t('measure.observation-2')}: `}
          <i style={observationStyle2}>{observationText2}</i>
        </div>
      </React.Fragment>)}

      {/* Status message */}
      {!valid && (<div>
        {`${t('measure.status')}: `}
        <i style={{color: "red"}}>{message}</i>
      </div>)}


    </div>
  );
};

export const MultiImageWindow = memo((props: any) => {
  const state = props.multiImageState as MultiImageOptions;
  const {multiImageOpen, setMultiImageOpen} = props;

  const classes = useStyles();
  const {viewer} = useViewer();
  const {t} = useTranslation();

  const [customPromptOpen, setCustomPromptOpen] = useState(false);
  const [customPromptState, setCustomPromptState] = useState({
    title: null,
    text: null,
    callback: () => {}
  });

  const onClose = () => {
    const result = viewer.multiImageClosePrompt();

    if (result) {
      setCustomPromptState({...result});
      setCustomPromptOpen(true);
    } else {
      setMultiImageOpen(false);
    }
  };

  const onRefModified = (container) => {
    if (!container) return;
    viewer.initMultiImageContainer(container);
  };

  const onNextImage = () => {
    viewer.multiImageNextImage();
  };

  const onPreviousImage = () => {
    viewer.multiImagePreviousImage();
  };

  const onComplete = () => {
    viewer.multiImageOnComplete();
  };

  const onAddPoint = () => {
    viewer.multiImageOnAddMore();
  };

  useEffect(() => {
    viewer?.toggleMultiImageWindow(multiImageOpen);
  }, [multiImageOpen]);

  const {canAddMore, canComplete, disableAddMore, disableComplete,
    disablePreviousImage, disableNextImage} = state;

  const textLines = [
    t('measure.click-to-add-observation-2')
  ];

  return (
    <React.Fragment>
      <DraggableDialog
        variant={'resize'}
        open={multiImageOpen}
        title={t('measure.select-matching-point')}
        onClose={onClose}
      >
        {/* Canvas element */}
        <CursorTooltip textLines={textLines}>
          <div ref={onRefModified} className={classes.gradient}/>
        </CursorTooltip>

        {state.intersect && (<MultiImageInfo
          state={state}
        />)}

        <div className={classes.buttonParentLeft}>

          {/* Previous Image */}
          <WindowButton
            text={t('measure.previous-image')}
            onClick={onPreviousImage}
            disabled={disablePreviousImage}
            color="secondary"
          />

          {/* Next Image */}
          <WindowButton
            text={t('measure.next-image')}
            onClick={onNextImage}
            disabled={disableNextImage}
            color="secondary"
          />

        </div>

        <div className={classes.buttonParentRight}>

          {/* Add Point */}
          <WindowButton
            text={t('measure.add-point')}
            hidden={!canAddMore}
            onClick={onAddPoint}
            disabled={disableAddMore}
            color="primary"
          />

          {/* Complete */}
          <WindowButton
            text={t('measure.complete')}
            hidden={!canComplete}
            onClick={onComplete}
            disabled={disableComplete}
            color="primary"
          />

        </div>

      </DraggableDialog>

      {/* Custom close prompt */}
      <PromptDialog
        open={customPromptOpen}
        onCancel={() => {
          setCustomPromptOpen(false);
        }}
        onSubmit={() => {
          setMultiImageOpen(false);
          setCustomPromptOpen(false);
          customPromptState.callback();
        }}
        title={customPromptState.title}
        prompt={customPromptState.text}
        button={t('buttons.discard')}
      />
    </React.Fragment>
  );
});